/**
 * Camera State Reducer
 * L4-ZOOM-003: State management for camera zoom and pan
 */

import {
  Point,
  CameraState,
  CameraConfig,
  INITIAL_CAMERA_STATE,
  clamp,
} from './camera-types';
import { calculateZoomAtPoint } from './camera-transforms';

/**
 * Camera action types
 */
export type CameraAction =
  | { type: 'ZOOM_AT_POINT'; targetZoom: number; screenAnchor: Point }
  | { type: 'ZOOM_IN' }
  | { type: 'ZOOM_OUT' }
  | { type: 'SET_ZOOM'; zoom: number }
  | { type: 'PAN'; deltaX: number; deltaY: number }
  | { type: 'SET_PAN'; panX: number; panY: number }
  | { type: 'RESET' };

/**
 * Camera state reducer
 *
 * Pure function that computes new camera state from action
 *
 * @param state - Current camera state
 * @param action - Action to apply
 * @param config - Camera configuration with limits
 * @returns New camera state
 */
/**
 * Clamp pan values so the background image always fills the viewport.
 *
 * The viewport in world space spans:
 *   left  = -panX / zoom
 *   right = (canvasWidth - panX) / zoom
 *   top   = -panY / zoom
 *   bottom = (canvasHeight - panY) / zoom
 *
 * We need: bgLeft <= viewLeft AND bgRight >= viewRight (same for Y).
 * Solving for panX/panY gives min/max bounds.
 */
function clampPanToBgBounds(
  panX: number,
  panY: number,
  zoom: number,
  config: CameraConfig
): { panX: number; panY: number } {
  if (!config.bgWorldBounds) return { panX, panY };

  const { left, right, top, bottom } = config.bgWorldBounds;

  // panX bounds: canvasWidth - right * zoom <= panX <= -left * zoom
  const minPanX = config.canvasWidth - right * zoom;
  const maxPanX = -left * zoom;

  // panY bounds: canvasHeight - bottom * zoom <= panY <= -top * zoom
  const minPanY = config.canvasHeight - bottom * zoom;
  const maxPanY = -top * zoom;

  // When content is larger than viewport: clamp so content always fills viewport
  // When content is smaller than viewport: lock to center (no drift)
  const clampedPanX = minPanX <= maxPanX
    ? clamp(panX, minPanX, maxPanX)
    : (minPanX + maxPanX) / 2; // center-lock
  const clampedPanY = minPanY <= maxPanY
    ? clamp(panY, minPanY, maxPanY)
    : (minPanY + maxPanY) / 2; // center-lock

  return { panX: clampedPanX, panY: clampedPanY };
}

export function cameraReducer(
  state: CameraState,
  action: CameraAction,
  config: CameraConfig
): CameraState {
  switch (action.type) {
    case 'ZOOM_AT_POINT': {
      const result = calculateZoomAtPoint(
        state,
        action.targetZoom,
        action.screenAnchor,
        config
      );
      const panned = clampPanToBgBounds(result.panX, result.panY, result.zoom, config);
      return { ...result, ...panned };
    }

    case 'ZOOM_IN': {
      const newZoom = clamp(
        state.zoom + config.zoomStep,
        config.minZoom,
        config.maxZoom
      );
      const panned = clampPanToBgBounds(state.panX, state.panY, newZoom, config);
      return { ...state, zoom: newZoom, ...panned };
    }

    case 'ZOOM_OUT': {
      const newZoom = clamp(
        state.zoom - config.zoomStep,
        config.minZoom,
        config.maxZoom
      );
      const panned = clampPanToBgBounds(state.panX, state.panY, newZoom, config);
      return { ...state, zoom: newZoom, ...panned };
    }

    case 'SET_ZOOM': {
      const newZoom = clamp(action.zoom, config.minZoom, config.maxZoom);
      const panned = clampPanToBgBounds(state.panX, state.panY, newZoom, config);
      return { ...state, zoom: newZoom, ...panned };
    }

    case 'PAN': {
      const panned = clampPanToBgBounds(
        state.panX + action.deltaX,
        state.panY + action.deltaY,
        state.zoom,
        config
      );
      return { ...state, ...panned };
    }

    case 'SET_PAN': {
      const panned = clampPanToBgBounds(action.panX, action.panY, state.zoom, config);
      return { ...state, ...panned };
    }

    case 'RESET': {
      return INITIAL_CAMERA_STATE;
    }

    default: {
      // Return current state for unknown actions
      return state;
    }
  }
}

/**
 * Action creators for cleaner API
 */
export const cameraActions = {
  zoomAtPoint: (targetZoom: number, screenAnchor: Point): CameraAction => ({
    type: 'ZOOM_AT_POINT',
    targetZoom,
    screenAnchor,
  }),

  zoomIn: (): CameraAction => ({
    type: 'ZOOM_IN',
  }),

  zoomOut: (): CameraAction => ({
    type: 'ZOOM_OUT',
  }),

  setZoom: (zoom: number): CameraAction => ({
    type: 'SET_ZOOM',
    zoom,
  }),

  pan: (deltaX: number, deltaY: number): CameraAction => ({
    type: 'PAN',
    deltaX,
    deltaY,
  }),

  setPan: (panX: number, panY: number): CameraAction => ({
    type: 'SET_PAN',
    panX,
    panY,
  }),

  reset: (): CameraAction => ({
    type: 'RESET',
  }),
};
